import React from 'react';
import { Editor } from '@tiptap/react';
import {
    Bold,
    Italic,
    List,
    ListOrdered,
    Quote,
    Undo,
    Redo,
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    Strikethrough,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Minus,
    ImageIcon,
    Link as LinkIcon,
    Unlink,
    Table,
    Trash2,
    Rows,
    Columns,
    Plus,
    Loader2
} from 'lucide-react';
import { uploadFile } from '@/services/upload';
import { EditorButton } from './editor-button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface MenuBarProps {
    editor: Editor | null;
}

export const MenuBar = ({ editor }: MenuBarProps) => {
    if (!editor) {
        return null;
    }

    const [isUploading, setIsUploading] = React.useState(false);

    const addImage = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e: any) => {
            const file = e.target.files[0];
            if (file) {
                setIsUploading(true);
                try {
                    const url = await uploadFile(file);
                    (editor.chain() as any).focus().setImage({ src: url }).run();
                } catch (error) {
                    console.error('Failed to upload image:', error);
                    alert('Failed to upload image. Please try again.');
                } finally {
                    setIsUploading(false);
                }
            }
        };
        input.click();
    };

    const [linkUrl, setLinkUrl] = React.useState('');
    const [tableRows, setTableRows] = React.useState(3);
    const [tableCols, setTableCols] = React.useState(3);
    const [linkOpen, setLinkOpen] = React.useState(false);
    const [tableOpen, setTableOpen] = React.useState(false);

    const handleSetLink = () => {
        if (linkUrl === '') {
            (editor.chain() as any).focus().extendMarkRange('link').unsetLink().run();
        } else {
            (editor.chain() as any).focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
        }
        setLinkUrl('');
        setLinkOpen(false);
    };

    const handleInsertTable = () => {
        if (tableRows > 0 && tableCols > 0) {
            (editor.chain() as any).focus().insertTable({ rows: tableRows, cols: tableCols }).run();
        }
        setTableOpen(false);
    };

    const isInTable = editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader');

    return (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-muted/40">
            <EditorButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                tooltip="Bold"
            >
                <Bold className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                tooltip="Italic"
            >
                <Italic className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive('strike')}
                tooltip="Strikethrough"
            >
                <Strikethrough className="h-4 w-4" />
            </EditorButton>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                isActive={editor.isActive('heading', { level: 1 })}
                tooltip="Heading 1"
            >
                <Heading1 className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                isActive={editor.isActive('heading', { level: 2 })}
                tooltip="Heading 2"
            >
                <Heading2 className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                isActive={editor.isActive('heading', { level: 3 })}
                tooltip="Heading 3"
            >
                <Heading3 className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                isActive={editor.isActive('heading', { level: 4 })}
                tooltip="Heading 4"
            >
                <Heading4 className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                isActive={editor.isActive('heading', { level: 5 })}
                tooltip="Heading 5"
            >
                <Heading5 className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                isActive={editor.isActive('heading', { level: 6 })}
                tooltip="Heading 6"
            >
                <Heading6 className="h-4 w-4" />
            </EditorButton>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <EditorButton
                onClick={() => (editor.chain() as any).focus().setTextAlign('left').run()}
                isActive={editor.isActive({ textAlign: 'left' })}
                tooltip="Align Left"
            >
                <AlignLeft className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => (editor.chain() as any).focus().setTextAlign('center').run()}
                isActive={editor.isActive({ textAlign: 'center' })}
                tooltip="Align Center"
            >
                <AlignCenter className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => (editor.chain() as any).focus().setTextAlign('right').run()}
                isActive={editor.isActive({ textAlign: 'right' })}
                tooltip="Align Right"
            >
                <AlignRight className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => (editor.chain() as any).focus().setTextAlign('justify').run()}
                isActive={editor.isActive({ textAlign: 'justify' })}
                tooltip="Align Justify"
            >
                <AlignJustify className="h-4 w-4" />
            </EditorButton>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <EditorButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                tooltip="Bullet List"
            >
                <List className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive('orderedList')}
                tooltip="Ordered List"
            >
                <ListOrdered className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive('blockquote')}
                tooltip="Blockquote"
            >
                <Quote className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                tooltip="Horizontal Rule"
            >
                <Minus className="h-4 w-4" />
            </EditorButton>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <Popover open={linkOpen} onOpenChange={(open) => {
                setLinkOpen(open);
                if (open) setLinkUrl(editor.getAttributes('link').href || '');
            }}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(editor.isActive('link') && 'bg-accent text-accent-foreground')}
                            >
                                <LinkIcon className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p className='text-xs'>Insert/Edit Link</p>
                    </TooltipContent>
                </Tooltip>
                <PopoverContent className="w-80 p-4" side="bottom" align="start">
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="link-url">URL</Label>
                            <Input
                                id="link-url"
                                placeholder="https://example.com"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSetLink();
                                }}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSetLink} className="flex-1">
                                Set Link
                            </Button>
                            {editor.isActive('link') && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        (editor.chain() as any).focus().unsetLink().run();
                                        setLinkUrl('');
                                    }}
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <EditorButton
                onClick={addImage}
                disabled={isUploading}
                tooltip="Upload Image"
            >
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            </EditorButton>

            <Popover open={tableOpen} onOpenChange={setTableOpen}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(isInTable && "bg-accent text-accent-foreground")}
                            >
                                <Table className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p className='text-xs'>{isInTable ? 'Table Options' : 'Insert Table'}</p>
                    </TooltipContent>
                </Tooltip>
                <PopoverContent className={cn("p-2", isInTable ? "w-48" : "w-56 p-4")} side="bottom" align="start">
                    {isInTable ? (
                        <div className="flex flex-col gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2"
                                onClick={() => {
                                    (editor.chain() as any).focus().addRowAfter().run();
                                    setTableOpen(false);
                                }}
                            >
                                <Rows className="h-3.5 w-3.5" />
                                <span>Add Row</span>
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                    (editor.chain() as any).focus().deleteRow().run();
                                    setTableOpen(false);
                                }}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span>Delete Row</span>
                            </Button>
                            <div className="h-px bg-border my-1" />
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2"
                                onClick={() => {
                                    (editor.chain() as any).focus().addColumnAfter().run();
                                    setTableOpen(false);
                                }}
                            >
                                <Columns className="h-3.5 w-3.5" />
                                <span>Add Column</span>
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                    (editor.chain() as any).focus().deleteColumn().run();
                                    setTableOpen(false);
                                }}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span>Delete Column</span>
                            </Button>
                            <div className="h-px bg-border my-1" />
                            <Button
                                variant="ghost"
                                size="sm"
                                className="justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 font-medium"
                                onClick={() => {
                                    (editor.chain() as any).focus().deleteTable().run();
                                    setTableOpen(false);
                                }}
                            >
                                <Table className="h-3.5 w-3.5" />
                                <span>Remove Table</span>
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="table-rows">Rows</Label>
                                    <Input
                                        id="table-rows"
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={tableRows}
                                        onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleInsertTable();
                                        }}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="table-cols">Cols</Label>
                                    <Input
                                        id="table-cols"
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={tableCols}
                                        onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleInsertTable();
                                        }}
                                    />
                                </div>
                            </div>
                            <Button size="sm" onClick={handleInsertTable} className="w-full">
                                Insert Table
                            </Button>
                        </div>
                    )}
                </PopoverContent>
            </Popover>

            <div className="w-px h-4 bg-border/60 mx-1" />

            <EditorButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                tooltip="Undo"
            >
                <Undo className="h-4 w-4" />
            </EditorButton>
            <EditorButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                tooltip="Redo"
            >
                <Redo className="h-4 w-4" />
            </EditorButton>
        </div>
    );
};
